const STORAGE_KEY = "gt_event_start_prices";
const TTL_MS = 60 * 60 * 1000;

type CacheEntry = { price: number; ts: number };
type CacheMap = Record<string, CacheEntry>;

const inFlightFetches = new Map<string, Promise<number | null>>();

/** Parses start_price from API or cache; returns 0 for sold out, null if missing/invalid. */
export function parseStartPrice(value: unknown): number | null {
  if (value == null || value === "") return null;
  const n =
    typeof value === "number"
      ? value
      : parseFloat(String(value).replace(/,/g, ""));
  return Number.isFinite(n) && n >= 0 ? n : null;
}

export function hasPositiveStartPrice(value: unknown): boolean {
  const parsed = parseStartPrice(value);
  return parsed != null && parsed > 0;
}

function readMap(): CacheMap {
  if (typeof window === "undefined") return {};
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as CacheMap) : {};
  } catch {
    return {};
  }
}

function writeMap(map: CacheMap) {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    // sessionStorage full or unavailable
  }
}

export function clearExpiredEventStartPrices() {
  if (typeof window === "undefined") return;
  const map = readMap();
  const now = Date.now();
  let changed = false;
  for (const id of Object.keys(map)) {
    if (now - map[id].ts > TTL_MS) {
      delete map[id];
      changed = true;
    }
  }
  if (changed) writeMap(map);
}

export function getCachedEventStartPrice(id: string | number): number | null {
  if (typeof window === "undefined") return null;
  clearExpiredEventStartPrices();
  const entry = readMap()[String(id)];
  if (!entry) return null;
  if (Date.now() - entry.ts > TTL_MS) return null;
  return entry.price;
}

export function setCachedEventStartPrice(id: string | number, price: unknown) {
  if (typeof window === "undefined") return;
  const parsed = parseStartPrice(price);
  if (parsed == null) return;
  const map = readMap();
  map[String(id)] = { price: parsed, ts: Date.now() };
  writeMap(map);
}

export function getInFlightEventStartPriceFetch(
  id: string,
): Promise<number | null> | undefined {
  return inFlightFetches.get(id);
}

export function setInFlightEventStartPriceFetch(
  id: string,
  promise: Promise<number | null>,
) {
  inFlightFetches.set(id, promise);
  promise.finally(() => {
    if (inFlightFetches.get(id) === promise) {
      inFlightFetches.delete(id);
    }
  });
}

/** Lowest price among in-stock tickets from filter-tickets; 0 when none available. */
export function minPriceFromAvailableTickets(tickets: unknown[]): number {
  if (!Array.isArray(tickets) || tickets.length === 0) return 0;

  const prices = tickets
    .filter((t: any) => t?.inStock === true)
    .map((t: any) =>
      parseStartPrice(t?.price_after_commission ?? t?.price),
    )
    .filter((p): p is number => p != null && p > 0);

  return prices.length > 0 ? Math.min(...prices) : 0;
}
